import { useQuery, useMutation, useQueryClient } from 'react-query';
import { positionUnwinderAxiosApi } from 'src/Config/axiosApi';
import {
  UnwindingRule,
  UnwindingExecution,
  CreateUnwindingRuleDto,
  UpdateUnwindingRuleDto,
  OrdersResponse
} from '../types/unwinding.types';
import { normalizeOrdersPayload } from '../utils/normalizeOrders';
import {
  getUnwinderState,
  stopUnwinder,
  startUnwinder,
  UnwinderState
} from 'src/Api/exchanges';

export function useUnwindingRules() {
  return useQuery<UnwindingRule[]>(
    ['unwinding-rules'],
    async () => {
      const { data } =
        await positionUnwinderAxiosApi.get<UnwindingRule[]>('/rules');
      return data;
    },
    { refetchInterval: 5000, keepPreviousData: true }
  );
}

export function useCreateRule() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: CreateUnwindingRuleDto) => {
      const { data } = await positionUnwinderAxiosApi.post<UnwindingRule>(
        '/rules',
        dto
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('unwinding-rules');
      }
    }
  );
}

export function useUpdateRule() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, dto }: { id: string; dto: UpdateUnwindingRuleDto }) => {
      const { data } = await positionUnwinderAxiosApi.patch<UnwindingRule>(
        `/rules/${id}`,
        dto
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('unwinding-rules');
      }
    }
  );
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      await positionUnwinderAxiosApi.delete(`/rules/${id}`);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('unwinding-rules');
      }
    }
  );
}

export function useActivateRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await positionUnwinderAxiosApi.post<UnwindingRule>(
        `/rules/${id}/activate`
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('unwinding-rules');
      }
    }
  );
}

export function usePauseRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await positionUnwinderAxiosApi.post<UnwindingRule>(
        `/rules/${id}/pause`
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('unwinding-rules');
      }
    }
  );
}

export function useRuleExecutions(ruleId: string | null) {
  return useQuery<UnwindingExecution[]>(
    ['unwinding-executions', ruleId],
    async () => {
      if (!ruleId) return [];
      const { data } = await positionUnwinderAxiosApi.get<
        UnwindingExecution[]
      >(`/rules/${ruleId}/executions`);
      return data;
    },
    {
      enabled: !!ruleId,
      refetchInterval: 10000,
      keepPreviousData: true
    }
  );
}

export function usePanic() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await positionUnwinderAxiosApi.post('/rules/panic');
      return data;
    },
    { onSuccess: () => { qc.invalidateQueries('unwinding-rules'); } }
  );
}

export function useResume() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await positionUnwinderAxiosApi.post('/rules/resume');
      return data;
    },
    { onSuccess: () => { qc.invalidateQueries('unwinding-rules'); } }
  );
}

export function useGlobalToggle() {
  const qc = useQueryClient();
  return useMutation(
    async (enabled: boolean) => {
      const { data } = await positionUnwinderAxiosApi.post('/rules/global-toggle', { enabled });
      return data;
    },
    { onSuccess: () => { qc.invalidateQueries('unwinding-rules'); } }
  );
}

export function useTogglePlatform() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, platform, enabled }: { id: string; platform: 'kalshi' | 'polymarket'; enabled: boolean }) => {
      const { data } = await positionUnwinderAxiosApi.post(`/rules/${id}/toggle-platform`, { platform, enabled });
      return data;
    },
    { onSuccess: () => { qc.invalidateQueries('unwinding-rules'); } }
  );
}

export function useSetSimParams() {
  return useMutation(
    async ({
      pairId,
      tna,
      size,
      maxVol,
    }: {
      pairId: string;
      tna: number;
      size: number;
      maxVol?: number;
    }) => {
      const { data } = await positionUnwinderAxiosApi.post('/rules/sim-params', {
        pairId,
        tna,
        size,
        maxVol,
      });
      return data;
    }
  );
}

export function useApplySim() {
  return useMutation(async ({ pairId }: { pairId: string }) => {
    const { data } = await positionUnwinderAxiosApi.post(
      '/rules/apply-sim',
      { pairId }
    );
    return data;
  });
}

export function useOrders() {
  return useQuery<OrdersResponse>(
    'unwinding-orders',
    async () => {
      const { data } = await positionUnwinderAxiosApi.get('/orders');
      return normalizeOrdersPayload(data);
    },
    { refetchInterval: 3000, keepPreviousData: true }
  );
}

export function useUnwinderState() {
  return useQuery<UnwinderState>(
    ['unwinder-state'],
    getUnwinderState,
    { refetchInterval: 10_000, keepPreviousData: true }
  );
}

export function useStopUnwinder() {
  const qc = useQueryClient();
  return useMutation(stopUnwinder, {
    onSuccess: job => {
      // Seed the system/job cache so the shared stepper picks it up immediately
      // (UpdatesProvider polls 'system/job' and starts the 2s loop once jobData
      // is a running job).
      qc.setQueryData('system/job', job);
      qc.invalidateQueries('system/job');
      qc.invalidateQueries('unwinding-rules');
      qc.invalidateQueries('unwinding-orders');
      qc.invalidateQueries(['unwinder-state']);
    }
  });
}

export function useStartUnwinder() {
  const qc = useQueryClient();
  return useMutation(startUnwinder, {
    onSuccess: job => {
      qc.setQueryData('system/job', job);
      qc.invalidateQueries('system/job');
      qc.invalidateQueries(['unwinder-state']);
    }
  });
}
